import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Turnstile } from '@marsidev/react-turnstile';
import { Send, CheckCircle, AlertCircle, MapPin, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const TURNSTILE_SITE_KEY = '0x4AAAAAACq8Yevbu1OzKA6t';

export const ContactForm = () => {
  const { t, i18n } = useTranslation();
  const turnstileRef = useRef(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!turnstileToken) {
      setStatus('error');
      setStatusMsg(t('contact.captcha_error'));
      return;
    }
    setLoading(true);
    setStatus(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone || null,
          subject: form.subject,
          message: form.message,
          language: i18n.language || 'en',
          turnstile_token: turnstileToken,
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setStatus('success');
        setStatusMsg(t('contact.success'));
        setForm({ name: '', email: '', phone: '', subject: '', message: '' });
        setTurnstileToken(null);
        if (turnstileRef.current) turnstileRef.current.reset();
      } else {
        setStatus('error');
        setStatusMsg(data.detail || t('contact.error'));
        if (turnstileRef.current) turnstileRef.current.reset();
        setTurnstileToken(null);
      }
    } catch {
      setStatus('error');
      setStatusMsg(t('contact.error'));
      if (turnstileRef.current) turnstileRef.current.reset();
      setTurnstileToken(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="contact-section" id="contact" data-testid="contact-section">
      <div className="container">
        <div className="contact-inner">
          <div className="contact-form-side">
            <p className="section-label">{t('contact.section_label')}</p>
            <h2>{t('contact.title')}</h2>
            <p className="contact-form-subtitle">{t('contact.subtitle')}</p>

            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="contact-name">{t('contact.name')}</label>
                  <input id="contact-name" className="form-input" type="text" name="name" value={form.name}
                    onChange={handleChange} placeholder={t('contact.name')} required data-testid="contact-form-name-input" />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="contact-email">{t('contact.email')}</label>
                  <input id="contact-email" className="form-input" type="email" name="email" value={form.email}
                    onChange={handleChange} placeholder={t('contact.email')} required data-testid="contact-form-email-input" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="contact-phone">{t('contact.phone')}</label>
                  <input id="contact-phone" className="form-input" type="tel" name="phone" value={form.phone}
                    onChange={handleChange} placeholder="+1 234 567 890" data-testid="contact-form-phone-input" />
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="contact-subject">{t('contact.subject')}</label>
                  <input id="contact-subject" className="form-input" type="text" name="subject" value={form.subject}
                    onChange={handleChange} placeholder={t('contact.subject')} required data-testid="contact-form-subject-input" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="contact-message">{t('contact.message')}</label>
                <textarea id="contact-message" className="form-textarea" name="message" value={form.message}
                  onChange={handleChange} placeholder={t('contact.message')} required data-testid="contact-form-message-textarea" />
              </div>
              <div className="turnstile-wrapper">
                <Turnstile ref={turnstileRef} siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={token => setTurnstileToken(token)}
                  onError={() => setTurnstileToken(null)}
                  onExpire={() => setTurnstileToken(null)}
                  options={{ theme: 'dark', size: 'normal' }}
                  data-testid="contact-form-turnstile" />
              </div>
              {status === 'success' && (
                <div className="form-success" data-testid="contact-form-success">
                  <CheckCircle size={18} /> {statusMsg}
                </div>
              )}
              {status === 'error' && (
                <div className="form-error" data-testid="contact-form-error">
                  <AlertCircle size={18} /> {statusMsg}
                </div>
              )}
              <button type="submit" className="form-submit-btn" disabled={loading} data-testid="contact-form-submit-button">
                <Send size={15} />
                {loading ? t('contact.sending') : t('contact.send')}
              </button>
            </form>
          </div>

          <div className="contact-info-side">
            <div className="contact-info-block">
              <h4>{t('contact.address_title')}</h4>
              <p>
                <MapPin size={14} style={{ display: 'inline', marginRight: 6, color: 'var(--color-gold)' }} />
                {t('contact.address')}
              </p>
              <p style={{ marginTop: 12 }}>
                <Mail size={14} style={{ display: 'inline', marginRight: 6, color: 'var(--color-gold)' }} />
                <a href="mailto:info@legend-spa.me">{t('contact.email_label')}: info@legend-spa.me</a>
              </p>
            </div>
            <div className="contact-promise">
              <p className="contact-promise-title">{t('contact.promise_quote')}</p>
              <p>{t('contact.promise_body')}</p>
            </div>
            <div className="contact-info-block">
              <h4>Legend Spa Hotel</h4>
              <p style={{ whiteSpace: 'pre-line' }}>{t('contact.hotel_info')}</p>
              <p style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--color-text-dim)' }}>
                {t('contact.opening')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
